/**
 * 5-card poker hand evaluation for Poli Poker.
 *
 * Category values are deliberately 0..9 so they double as the game's
 * "hand value" score component (see scoring.ts and the official scoring doc):
 *   0 Zipper (high card) ... 9 Royal Flush.
 */
import type { Card, Rank, Suit } from "./cards.ts";

/**
 * Suit ranking for the "there is never a tie" rule: when two hands are equal on
 * category and card ranks, the higher suit on the top card decides.
 * Default: Spades > Hearts > Diamonds > Clubs (confirm order with Scott).
 */
export const SUIT_RANK: Record<Suit, number> = { S: 4, H: 3, D: 2, C: 1 };

export const HandCategory = {
  Zipper: 0, // high card / nothing
  Pair: 1,
  TwoPair: 2,
  ThreeOfAKind: 3,
  Straight: 4,
  Flush: 5,
  FullHouse: 6,
  FourOfAKind: 7,
  StraightFlush: 8,
  RoyalFlush: 9,
} as const;
export type HandCategory = (typeof HandCategory)[keyof typeof HandCategory];

export const CATEGORY_NAME: Record<HandCategory, string> = {
  [HandCategory.Zipper]: "Zipper",
  [HandCategory.Pair]: "One Pair",
  [HandCategory.TwoPair]: "Two Pair",
  [HandCategory.ThreeOfAKind]: "Three of a Kind",
  [HandCategory.Straight]: "Straight",
  [HandCategory.Flush]: "Flush",
  [HandCategory.FullHouse]: "Full House",
  [HandCategory.FourOfAKind]: "Four of a Kind",
  [HandCategory.StraightFlush]: "Straight Flush",
  [HandCategory.RoyalFlush]: "Royal Flush",
};

export interface HandRank {
  category: HandCategory;
  /** Tiebreak ranks, most significant first (e.g. pair rank, then kickers). */
  tiebreak: number[];
  /** Final suit-aware tiebreak: cards encoded (rank*4 + suitRank), high→low. */
  suitKey: number[];
}

/** >0 if `a` beats `b`. Never returns 0 for two distinct 5-card hands. */
export function compareHands(a: HandRank, b: HandRank): number {
  if (a.category !== b.category) return a.category - b.category;
  const len = Math.max(a.tiebreak.length, b.tiebreak.length);
  for (let i = 0; i < len; i++) {
    const d = (a.tiebreak[i] ?? 0) - (b.tiebreak[i] ?? 0);
    if (d !== 0) return d;
  }
  // Ranks identical → suit order of cards decides (house rule: no ties).
  for (let i = 0; i < a.suitKey.length; i++) {
    const d = (a.suitKey[i] ?? 0) - (b.suitKey[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/**
 * Evaluate exactly 5 cards. (Whiskey Poker hands are always 5 cards, so no
 * best-of-7 selection is needed.)
 */
export function evaluate(cards: readonly Card[]): HandRank {
  if (cards.length !== 5) {
    throw new Error(`evaluate() expects 5 cards, got ${cards.length}`);
  }

  // (Written without Maps/sorts of objects: the AI evaluates millions of hands per decision batch.
  // Output is identical to the original implementation — checked by the AI test suite.)
  const ranks: number[] = [cards[0].rank, cards[1].rank, cards[2].rank, cards[3].rank, cards[4].rank];
  sortDesc(ranks);
  const s0 = cards[0].suit;
  const isFlush = cards[1].suit === s0 && cards[2].suit === s0 && cards[3].suit === s0 && cards[4].suit === s0;

  // Rank multiplicities, then ranks grouped by count desc, rank desc (e.g. full house: trips, pair).
  const counts = new Uint8Array(15);
  for (let i = 0; i < 5; i++) counts[ranks[i]]++;
  const byGroupRank: number[] = [];
  let shape = "";
  for (let n = 4; n >= 1; n--) for (let r = 14; r >= 2; r--) if (counts[r] === n) { byGroupRank.push(r); shape += n; }

  // Straight detection (including the A-2-3-4-5 "wheel").
  let straightHigh = 0;
  if (byGroupRank.length === 5) {
    if (ranks[0] - ranks[4] === 4) straightHigh = ranks[0];
    else if (ranks[0] === 14 && ranks[1] === 5 && ranks[4] === 2) straightHigh = 5; // wheel
  }
  const isStraight = straightHigh > 0;

  // Suit-aware key: each card as rank*4 + suitRank, sorted high→low.
  const suitKey: number[] = [];
  for (let i = 0; i < 5; i++) suitKey.push(cards[i].rank * 4 + SUIT_RANK[cards[i].suit]);
  sortDesc(suitKey);

  let category: HandCategory;
  let tiebreak: number[];
  if (isStraight && isFlush) {
    category = straightHigh === 14 ? HandCategory.RoyalFlush : HandCategory.StraightFlush;
    tiebreak = [straightHigh];
  } else if (shape === "41") {
    category = HandCategory.FourOfAKind;
    tiebreak = byGroupRank;
  } else if (shape === "32") {
    category = HandCategory.FullHouse;
    tiebreak = byGroupRank;
  } else if (isFlush) {
    category = HandCategory.Flush;
    tiebreak = ranks as Rank[];
  } else if (isStraight) {
    category = HandCategory.Straight;
    tiebreak = [straightHigh];
  } else if (shape === "311") {
    category = HandCategory.ThreeOfAKind;
    tiebreak = byGroupRank;
  } else if (shape === "221") {
    category = HandCategory.TwoPair;
    tiebreak = byGroupRank;
  } else if (shape === "2111") {
    category = HandCategory.Pair;
    tiebreak = byGroupRank;
  } else {
    category = HandCategory.Zipper;
    tiebreak = ranks as Rank[];
  }
  return { category, tiebreak, suitKey };
}

/** In-place descending insertion sort (5 elements). */
function sortDesc(a: number[]): void {
  for (let i = 1; i < a.length; i++) {
    const x = a[i];
    let j = i - 1;
    while (j >= 0 && a[j] < x) { a[j + 1] = a[j]; j--; }
    a[j + 1] = x;
  }
}
